import { CommonModule } from "@angular/common";
import { HttpResponse } from "@angular/common/http";
import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
  inject,
} from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { MessageService } from "primeng/api";
import {
  Observable,
  Subject,
  Subscription,
  distinctUntilChanged,
  filter,
  skip,
  switchMap,
  takeUntil,
  tap,
} from "rxjs";
import { Layouts } from "..";
import { DeleteConfirmationComponent } from "../../components/modal-templates/delete-confirmation/delete-confirmation.component";
import { GenericFormModalComponent } from "../../components/modal-templates/generic-form-modal/generic-form-modal.component";
import { EditorItemModalComponent } from "../../components/modal-templates/generic-modal/editor-item-modal.component";
import { ScriptMappingComponent } from "../../components/modal-templates/script-mapping/script-mapping.component";
import { SharedImports } from "../../components/shared.module";
import { TreeViewComponent } from "../../components/tree-view/tree-view.component";
import { Actions } from "../../core/enums/automation.enum";
import { IBreadcrumb } from "../../core/models/common";
import { TreeNode } from "../../core/models/treeNode";
import { AutomationActions } from "../../core/ngrx/actions/automation.actions";
import {
  selectAllServiceOperations,
  selectAllServices,
  selectApplcationsAndPages,
  selectApplications,
  selectAutomationError,
  selectCombinedReusableComponent,
  selectCombinedTestSuiteComponent,
  selectLoading,
  selectPages,
  selectReusableScript,
  selectScriptGroup,
  selectTestScript,
  selectUiObjects
} from "../../core/ngrx/selectors/automation.selectors";
import {
  getApplicationDetails,
  getReusableComponentsDetails,
  getServicesDetails,
  getStandardTestSuitsDetails,
  initialProjectStructure,
  projectCardTile
} from "../../core/tree/script-generator";
import { UtilitiesService } from "../../core/services/utilities.service";
import { MessageEnum } from "../../core/utils/message.enum";
import { BaseLayoutComponent } from "../base-layout.component";

@Component({
  selector: "app-automatic-script-generator",
  standalone: true,
  imports: [...SharedImports, CommonModule],
  templateUrl: "./automatic-script-generator.component.html",
  styleUrl: "./automatic-script-generator.component.scss",
})
export class AutomaticScriptGeneratorComponent
  extends BaseLayoutComponent
  implements OnInit, OnDestroy
{
  /** Completes on ngOnDestroy to unsubscribe takeUntil-based streams. */
  private readonly destroy$ = new Subject<void>();
  /** Collects all manual Subscription objects so they can be torn down together. */
  private readonly subscriptions = new Subscription();

  treeData: any[] = [];
  scriptCards = [];
  projectId = "";
  isLoading: boolean = false;
  applications$!: Observable<any>;
  pages$!: Observable<any>;
  uiObjects$!: Observable<any>;
  scriptGroup$!: Observable<any>;
  scriptTestScript$!: Observable<any>;
  selectCombinedReusableComponent$!: Observable<any>;
  selectCombinedTestSuiteComponent$!: Observable<any>;
  selectApplcationsAndPage$!: Observable<any>;
  reusableScript$!: Observable<any>;
  loading$!: Observable<boolean>;
  error$!: Observable<string | null>;

  @ViewChild("appGenericView", { read: ViewContainerRef })
  appGenericView!: ViewContainerRef;
  selectedItem: any;
  selectCardType: string = "";
  private modalService = inject(NgbModal);
  loading: boolean = false;
  automationProjectID: any;
  applicationId!: string;
  pageId!: string;
  filteredNodes: TreeNode[] = [];

  displayEditorHeader = false;
  breadcrumb: IBreadcrumb[] = [];
  testSuits: any[] = [];
  reusableComponents: any[] = [];
  isTestcaseMapping: boolean = false;
  isDownloading: boolean = false;
  isExporting = false;
  isImporting = false;
  private pageActionSub?: Subscription;
  absoluteRootNode: any;
  apiServices$!: Observable<any>;
  serviceOperations$!: Observable<any>;
  serviceComponents: any[] = [];
  private messageService = inject(MessageService);
  private utils = inject(UtilitiesService);
  @ViewChild(TreeViewComponent) treeViewComponent!: TreeViewComponent;
  selectedFramworkType!: string;
  isFrameworkTypeLoading: boolean = true;
  serviceId!: any;
  operationId!: any;

  ngOnInit(): void {
    this.breadcrumbService.getBreadcrumb().subscribe((data: IBreadcrumb[]) => {
      if (data) {
        this.breadcrumb = data;
      }
    });

    this.breadcrumbService.onNodeClick().subscribe((data: IBreadcrumb) => {
      if (data && data.meta?.treeNode && this.selectedItem?.level) {
        this.treeViewComponent.toggleExpand(data.meta.treeNode);
      }
    });

    this.breadcrumbService.setPageActions([
      {
        key: "importExport",
        label: "Import / Export",
        variant: "primary",
        isDropdown: true,
        menuItems: [
          { key: "importAutomationProject", label: "Import" },
          { key: "exportAutomationProject", label: "Export" },
        ],
      },
    ]);
    this.pageActionSub = this.breadcrumbService.onPageAction().subscribe((key: string) => {
      if (key === "importAutomationProject") {
        this.openImport();
      } else if (key === "exportAutomationProject") {
        this.openExport();
      }
    });

    this.projectId = this.dataStore?.data?.id;
    this.projectService.getAutomationCardType().subscribe((type: any) => {
      this.selectCardType = type;
    });
    this.loadInitialProjct();

    // A3-5490: re-initialise when the user navigates to a different project.
    // skip(1) skips the initial BehaviorSubject replay so we don't double-load.
    this.subscriptions.add(
      this.projectService.getProjectId().pipe(
        distinctUntilChanged(),
        skip(1),
        filter((id): id is string => !!id),
        takeUntil(this.destroy$)
      ).subscribe((newProjectId) => {
        this.projectId = newProjectId;
        this.resetGeneratorView();
        this.loadInitialProjct();
      })
    );

    this.applications$ = this.projectService.getAutomationProjectId().pipe(
      filter((id: any) => !!id),
      tap((id: string) => (this.automationProjectID = id)),
      switchMap((id: string) => this.store.select(selectApplications(id)))
    );

    this.pages$ = this.projectService.getApplicationId().pipe(
      filter((id: any) => !!id),
      tap((id: string) => (this.applicationId = id)),
      switchMap((id: string) => this.store.select(selectPages(id)))
    );

    this.uiObjects$ = this.projectService.getPageId().pipe(
      filter((id: any) => !!id),
      tap((id: string) => (this.pageId = id)),
      switchMap((id: string) => this.store.select(selectUiObjects(id)))
    );

    this.scriptGroup$ = this.store.select(selectScriptGroup);
    this.scriptTestScript$ = this.store.select(selectTestScript);
    this.selectCombinedReusableComponent$ = this.store.select(
      selectCombinedReusableComponent
    );
    this.selectCombinedTestSuiteComponent$ = this.store.select(
      selectCombinedTestSuiteComponent
    );
    this.selectApplcationsAndPage$ = this.store.select(
      selectApplcationsAndPages
    );
    this.reusableScript$ = this.store.select(selectReusableScript);

    this.apiServices$ = this.store.select(
      selectAllServices
    );
    this.serviceOperations$ = this.store.select(
      selectAllServiceOperations
    );
    this.loading$ = this.store.select(selectLoading);
    this.error$ = this.store.select(selectAutomationError);

    this.loadNgrxData();
  }

  loadNgrxData() {
    this.subscriptions.add(this.applications$.subscribe((item) => {
      console.log("APPLICATION DETAILS");
      let applicationList = [];
      if (item?.length) {
        applicationList = item.map((item: any) => ({
          ...item,
          displayName: item?.name,
          // icon: "apps",
          order: 1,
          level: 2,
          type: "application",
          // isAdd: true,
          view: "applicationsView",
          children: [],
          // menu: ["Edit", "Delete"],
          addApplicationTemplate: [
            {
              name: "Page Name",
              dataType: "string",
              id: "pageName",
              placeholder: "Page Name",
            },
            {
              name: "Page URL",
              dataType: "string",
              id: "url",
              placeholder: "Page URL",
            },
          ],
          editApplicationTemplate: [
            {
              name: "Application Name",
              dataType: "string",
              id: "name",
              placeholder: "Application Name",
            },
            {
              name: "Application Type",
              dataType: "type",
              id: "paradigm",
              placeholder: "Application Type",
            },
            {
              name: "Environment",
              dataType: "browser",
              id: "browser",
              placeholder: "Environment",
            },
          ],
        }));
      }
      if (this.selectCardType === "applications" && this.selectedItem?.children) {
        const appNode = this.selectedItem.children.find(
          (item: any) => item?.type === "applications"
        );
        if (appNode) {
          this.selectedItem = appNode;
        }
      }

      const appIndex = this.treeData.findIndex(
        (item: any) => item.type === "applications"
      );
      if (appIndex >= 0) {
        this.treeData[appIndex].children = applicationList;
      }
      this.automatiCScriptService.setActiveNode(this.selectedItem);
      this.loadComponent();
    }));

    this.subscriptions.add(this.selectApplcationsAndPage$.subscribe((data) => {
      console.log("Pages==>", data);
      // A3-5490: use the current automationProjectID as the key, not Object.keys()[0]
      // which always returns Project A's key when the store still holds Project A's data.
      const applicationsList = data?.applications?.[this.automationProjectID] ?? [];
      let combineApplicationsPages = [];
      if (applicationsList?.length) {
        combineApplicationsPages = applicationsList.map((item: any) => ({
          ...item,
          displayName: item?.name,
          // icon: "apps",
          order: 1,
          level: 2,
          type: "application",
          // isAdd: true,
          view: "applicationsView",
          children: this.loadPagesWithTemplates(data?.pages[item.id]),
          // menu: ["Edit", "Delete"],
          addApplicationTemplate: [
            {
              name: "Page Name",
              dataType: "string",
              id: "pageName",
              placeholder: "Page Name",
            },
            {
              name: "Page URL",
              dataType: "string",
              id: "url",
              placeholder: "Page URL",
            },
          ],
          editApplicationTemplate: [
            {
              name: "Application Name",
              dataType: "string",
              id: "name",
              placeholder: "Application Name",
            },
            {
              name: "Application Type",
              dataType: "type",
              id: "paradigm",
              placeholder: "Application Type",
            },
            {
              name: "Environment",
              dataType: "browser",
              id: "browser",
              placeholder: "Environment",
            },
          ],
        }));
      }

      console.log("All Applications", combineApplicationsPages);
      if (this.selectCardType === "applications" && this.selectedItem?.children) {
        const appNode = this.selectedItem.children.find(
          (item: any) => item?.type === "applications"
        );
        if (appNode) {
          this.selectedItem = appNode;
        }
      }

      const appIndex = this.treeData.findIndex(
        (item: any) => item.type === "applications"
      );
      if (appIndex >= 0) {
        this.treeData[appIndex].children = combineApplicationsPages;
      }
      this.automatiCScriptService.setActiveNode(this.selectedItem);
      this.loadComponent();
    }));

    this.subscriptions.add(this.scriptTestScript$.subscribe((item) => {
      console.log("Test Script", item[item.length - 1]);
      if (this.isTestcaseMapping && item?.length) {
        this.openTestScriptMappingView(item[item.length - 1]);
        this.isTestcaseMapping = false;
      }
    }));

    this.subscriptions.add(this.selectCombinedTestSuiteComponent$.subscribe((item) => {
      this.testSuits = [];
      console.log(item);
      if (item?.testSuites?.length) {
        this.testSuits = item?.testSuites.map((item: any) => ({
          ...item,
          displayName: item?.name,
          // icon: "folder",
          order: 1,
          level: 2,
          type: "test_script_group",
          // isAdd: true,
          // menu: ["Edit", "Delete"],
          view: "testSuiteView",
          children: [],
          addApplicationTemplate: [
            {
              name: "Test Script Name",
              dataType: "string",
              id: "name",
              placeholder: "Test Script Name",
            },
          ],
          editApplicationTemplate: [
            {
              name: "Test Suite Name",
              dataType: "string",
              id: "name",
              placeholder: "Test Suite Name",
            },
          ],
        }));
      }
      if (item?.testcases?.length) {
        let parentScripts = item?.testcases?.filter(
          (script: any) => !script.hasOwnProperty("automationScriptGroup")
        );
        parentScripts = parentScripts.map((item: any) => ({
          ...item,
          displayName: item?.name,
          // icon: "description",
          order: 1,
          level: 2,
          // isAdd: true,
          // menu: ["Edit", "Delete"],
          view: "testScriptView",
          editApplicationTemplate: [
            {
              name: "Test Script Name",
              dataType: "string",
              id: "name",
              placeholder: "Test Script Name",
            },
          ],
        }));
        this.testSuits = [...this.testSuits, ...parentScripts];

        let groupScripts = item?.testcases?.filter((script: any) =>
          script.hasOwnProperty("automationScriptGroup")
        );
        groupScripts?.forEach((item: any) => {
          const isExistingFolderIndex: any = this.testSuits?.findIndex(
            (data) => data.id === item?.automationScriptGroup?.id
          );
          if (isExistingFolderIndex >= 0) {
            this.testSuits[isExistingFolderIndex].children.push({
              ...item,
              level: 3,
              view: "testScriptView",
              // menu: ["Edit", "Delete"],
              // icon: "description",
              displayName: item?.name,
              editApplicationTemplate: [
                {
                  name: "Test Script Name",
                  dataType: "string",
                  id: "name",
                  placeholder: "Test Script Name",
                },
              ],
            });
          } else {
            this.testSuits.push({
              ...item,
              level: 3,
              view: "testScriptView",
              // menu: ["Edit", "Delete"],
              // icon: "description",
              displayName: item?.name,
            });
          }
        });
      }

      if (this.selectCardType === "Script_Group_Card" && this.selectedItem?.children) {
        const reusableComponentNode = this.selectedItem.children.find(
          (item: any) => item?.type === "script_group"
        );
        if (reusableComponentNode) {
          this.selectedItem = reusableComponentNode;
        }
      }

      const reusableIndex = this.treeData.findIndex(
        (item: any) => item.type === "script_group"
      );
      // A3-5490: guard against -1 when treeData was just reset (project switch)
      if (reusableIndex >= 0) {
        this.treeData[reusableIndex].children = this.testSuits;
      }
      this.automatiCScriptService.setActiveNode(this.selectedItem);
      this.dataStore.data = {
        ...this.dataStore.data,
        reuableComponentList: this.testSuits,
      };
      this.loadComponent();
    }));

    this.subscriptions.add(this.selectCombinedReusableComponent$.subscribe((item) => {
      console.log("Reusable Component", item);
      this.reusableComponents = [];
      if (item?.reusableComponent?.length) {
        this.reusableComponents = item?.reusableComponent.map((item: any) => ({
          ...item,
          displayName: item?.name,
          // icon: "folder",
          order: 1,
          level: 2,
          type: "reusable_component_folder",
          // isAdd: true,
          // menu: ["Edit", "Delete"],/]
          view: "reusableComponentView",
          children: [],
          addApplicationTemplate: [
            {
              name: "Reusable Component Name",
              dataType: "string",
              id: "name",
              placeholder: "Reusable Component Name",
            },
          ],
          editApplicationTemplate: [
            {
              id: "name",
              key: "folderName",
              name: "Reusable Components Folder Name",
              dataType: "string",
              placeholder: "Reusable Components Folder Name",
              required: false,
            },
          ],
        }));
      }
      if (item?.reusableScript?.length) {
        let parentScripts = item?.reusableScript?.filter(
          (script: any) => !script.hasOwnProperty("automationScriptGroup")
        );
        parentScripts = parentScripts.map((item: any) => ({
          ...item,
          displayName: item?.name,
          // icon: "description",
          order: 1,
          level: 4,
          type: "reusable_script_file",
          // isAdd: true,
          // menu: ["Edit", "Delete"],
          view: "reusableComponentView",
          editApplicationTemplate: [
            {
              name: "Reusable Component Name",
              dataType: "string",
              id: "name",
              placeholder: "Reusable Component Name",
            },
          ],
        }));
        this.reusableComponents = [
          ...this.reusableComponents,
          ...parentScripts,
        ];

        let groupScripts = item?.reusableScript?.filter((script: any) =>
          script.hasOwnProperty("automationScriptGroup")
        );
        groupScripts?.forEach((item: any) => {
          const isExistingFolderIndex: any = this.reusableComponents?.findIndex(
            (data) => data.id === item?.automationScriptGroup?.id
          );
          if (isExistingFolderIndex >= 0) {
            this.reusableComponents[isExistingFolderIndex].children.push({
              ...item,
              level: 3,
              view: "reusableTestScriptView",
              // menu: ["Edit", "Delete"],
              // icon: "description",
              displayName: item?.name,
              editApplicationTemplate: [
                {
                  name: "Reusable Component Name",
                  dataType: "string",
                  id: "name",
                  placeholder: "Reusable Component Name",
                },
              ],
            });
          } else {
            this.reusableComponents.push({
              ...item,
              level: 3,
              view: "reusableTestScriptView",
              // menu: ["Edit", "Delete"],
              // icon: "description",
              displayName: item?.name,
              editApplicationTemplate: [
                {
                  name: "Reusable Component Name",
                  dataType: "string",
                  id: "name",
                  placeholder: "Reusable Component Name",
                },
              ],
            });
          }
        });
      }

      if (this.selectCardType === "reusable_component" && this.selectedItem?.children) {
        const reusableComponentNode = this.selectedItem.children.find(
          (item: any) => item?.type === "reusable_component"
        );
        if (reusableComponentNode) {
          this.selectedItem = reusableComponentNode;
        }
      }

      const reusableIndex = this.treeData.findIndex(
        (item: any) => item.type === "reusable_component"
      );
      if (reusableIndex >= 0) {
        this.treeData[reusableIndex].children = this.reusableComponents;
      }
      this.automatiCScriptService.setActiveNode(this.selectedItem);
      this.loadComponent();
    }));

    this.subscriptions.add(this.apiServices$.subscribe((item) => {
      console.log("Services ", item);
      this.serviceComponents = [];
      if (item?.length) {
        this.serviceComponents = item?.map((item: any) => ({
          ...item,
          displayName: item?.name,
          order: 1,
          level: 2,
          type: "api-service-view",
          view: "restServiceOverviewView",
          addApplicationTemplate: [],
          children: [],
        }));
      }
      
      if (this.selectCardType === "api-service-view" && this.selectedItem?.children) {
        const serviceNode = this.selectedItem.children.find(
          (item: any) => item?.type === "api-service"
        );
        if (serviceNode) {
          this.selectedItem = serviceNode;
        }
      }
      
      const appIndex = this.treeData.findIndex(
        (item: any) => item.type === "api-service"
      );
      if (appIndex >= 0) {
        this.treeData[appIndex].children = [{
          id: "restApi",
          name: "Rest API Services",
          displayName: "Rest API Services",
          order: 1,
          level: 0,
          type: "default-label",
          active: true,
        }, ...this.serviceComponents];
      }
      this.automatiCScriptService.setActiveNode(this.selectedItem);
      this.loadComponent();
    }));

    this.subscriptions.add(this.serviceOperations$.subscribe((item) => {
      console.log("Service Operations ", item);
      if (item?.length) {
        let operationList:any = [];

        item?.forEach((item: any) => {
          const isExistingFolderIndex: any = this.serviceComponents?.findIndex(
            (data) => data.id === item?.automationServiceId
          );
          if (isExistingFolderIndex >= 0) {
            operationList.push({
              ...item,
            displayName: item?.name,
            level: 3,
            type: "operations",
            view: "restOperationsView",
            oprtationType: item?.type,
            addApplicationTemplate: [],
            });
            this.serviceComponents[isExistingFolderIndex].children= operationList;
          }
        });
      }

      if (this.selectCardType === "api-service-view" && this.selectedItem?.type !== "operations" && this.selectedItem?.type !== "api-service-view") {
        const serviceOpNode = this.selectedItem.children?.find(
          (item: any) => item?.type === "api-service-view"
        );
        if (serviceOpNode) {
          this.selectedItem = serviceOpNode;
        }
      }
      this.automatiCScriptService.setActiveNode(this.selectedItem);
      this.loadComponent();
    }));

  }

  loadPagesWithTemplates(item: any[]) {
    let pagesList = [];
    if (item?.length) {
      pagesList = item.map((item: any) => ({
        ...item,
        displayName: item?.name,
        // icon: "docs",
        order: 1,
        level: 3,
        type: "pages",
        // isAdd: true,
        // menu: ["Edit", "Delete"],
        view: "pageView",
        addApplicationTemplate: [
          {
            name: "UI Object Name",
            dataType: "string",
            id: "name",
            placeholder: "UI Object Name",
          },
        ],
        editApplicationTemplate: [
          {
            name: "Page Name",
            dataType: "string",
            id: "name",
            placeholder: "Page Name",
          },
          {
            name: "Page URL",
            dataType: "string",
            id: "url",
            placeholder: "Page URL",
          },
        ],
      }));
    }

    return pagesList;
  }

  openTestScriptMappingView(item: any) {
    const modalRef = this.modalService.open(ScriptMappingComponent, {
      centered: true,
      windowClass: "scrip-mapping-modal",
      backdrop: 'static',
    });
    modalRef.componentInstance.displayStep = "Step 2/2";
    if (item) {
      modalRef.componentInstance.data = {
        ...item,
        projectId: this.projectId,
        automationId: this.automationProjectID,
        scriptId: item?.id,
      };
    }
    modalRef.result.then((result) => {
      console.log(result);
      this.isTestcaseMapping = false;
    });
  }

  /**
   * Loads the component based on the selected menu item and updates the breadcrumb navigation.
   * It clears any existing components and dynamically creates the new component for the selected view.
   * @memberof SideNavContentComponent
   */
  loadComponent() {
    // A3-5490: do not attempt to render a view when selectedItem has been
    // cleared (e.g. immediately after a project switch).
    if (!this.selectedItem?.view) {
      return;
    }
    const componentFactory =
      this.componentFactoryResolver.resolveComponentFactory(
        this.getComponent(
          this.selectedItem?.view ? this.selectedItem?.view : null
        )
      );
    this.appGenericView?.clear();

    this.addBreadcrumbs();

    const componentRef = this.appGenericView?.createComponent(componentFactory);
    if (componentRef) {
      this.dataStore.activeNode = this.selectedItem;
      (componentRef?.instance as BaseLayoutComponent).dataStore =
        this.dataStore;

      componentRef.changeDetectorRef.detectChanges();
    }
  }

  /**
   * Rebuilds the whole breadcrumb from the selected node's real ancestors in
   * treeData. Building it incrementally (replacing only the node at the same
   * level) left stale parents when the user jumped directly to a node in a
   * different branch, e.g. from TS_BankDhofar > tc_X to LIB_Common > bc_Y.
   */
  addBreadcrumbs() {
    const selected = this.selectedItem;
    const isProjectView =
      selected?.level === 0 && selected?.view === "automationProjectView";

    this.breadcrumbService.clear();
    this.breadcrumbService.addNode({
      name: "Design",
      key: "design",
      isRootNode: true,
      clickable: false,
    });

    if (isProjectView) {
      this.breadcrumbService.addNode({
        name: "Automatic Script Generation",
        key: "testScriptGenerator",
        isRootNode: false,
        clickable: true,
        meta: { treeNode: selected },
      });
      return;
    }

    this.breadcrumbService.addNode({
      name: "Automatic Script Generation",
      key: "automaticScriptGeneration",
      isRootNode: false,
      clickable: false,
    });

    // Match by reference first; fall back to id + level in case the tree
    // emitted a copy of the node.
    const path = findTreePath<any>(
      this.treeData,
      (node) =>
        node === selected ||
        (node?.id != null &&
          node.id === selected?.id &&
          node.level === selected?.level)
    );
    const nodes = path.length ? path : [selected];
    nodes.forEach((node) =>
      this.breadcrumbService.addNode(this.toBreadcrumbNode(node))
    );
  }

  private toBreadcrumbNode(node: any): IBreadcrumb {
    const breadcrumbNode: IBreadcrumb = {
      name: node?.displayName,
      key: node?.id,
      isRootNode: false,
      clickable: true,
      meta: { treeNode: node },
    };
    if (node?.level !== 0) {
      breadcrumbNode.imgPath = this.getImgById(node?.id);
      breadcrumbNode.level = node?.level;
    }
    return breadcrumbNode;
  }

  getImgById(id: string): string {
    switch (id) {
      case "applications":
        return `${this.basePath}assets/images/script/manage.svg`;
      case "reusableComponents":
        return `${this.basePath}assets/images/script/reusable.svg`;
      case "standardTestSuite":
        return `${this.basePath}assets/images/script/test-script.svg`;
      case "dataConnection":
        return `${this.basePath}assets/images/script/data-conn.svg`;
      default:
        return `${this.basePath}assets/images/script/manage.svg`;
    }
  }

  /**
   * Returns the corresponding component for the selected view.
   * If the view is not found, it defaults to the `DefaultLayoutView`.
   * @param view - The view identifier.
   * @returns {Component} - The component to be loaded.
   */
  getComponent(view: any) {
    return view && Layouts[view as keyof typeof Layouts]
      ? Layouts[view as keyof typeof Layouts]
      : Layouts.DefaultLayoutView;
  }

  onMenuItemSelected(item: any) {
    if (item?.popupType === "add") {
      this.loadPopupTemplates(item);
    } else if (item?.popupType === "Edit") {
      this.renamePopupTemplates(item);
    } else if (item?.popupType === "Delete") {
      this.deleteItems(item);
    } else {
      // Keep the table page across edits/refreshes of the same node; start
      // from page 1 only when the user navigates to a different node.
      if (item?.id !== this.selectedItem?.id) {
        this.utils.clearPageIndexes();
      }
      this.selectedItem = item;
      this.automatiCScriptService.setActiveNode(this.selectedItem);
      if (this.selectedItem) {
        if (item?.type === "project") {
          this.projectService.setAutomationProjectId(item?.id);
        } else if (item?.type === "application") {
          this.projectService.setApplicationId(item?.id);
        } else if (item?.type === "pages") {
          this.projectService.setPageId(item?.id);
        } else if (item?.type === "api-service-view") {
          this.projectService.setServiceId(item?.id);
          this.serviceId = item?.id;
        }else if (item?.type === "operations") {
          this.projectService.setOperationId(item?.id);
          this.operationId = item?.id;
        }
        this.loadComponent();
        this.loadTreeData(item);
      }
    }
    console.log("ITEM", item);
    console.log("Selected ITEM", this.selectedItem);
  }

  loadPopupTemplates(item: any) {
    const template = [...(item?.addApplicationTemplate ?? [])];
    switch (item?.type) {
      case "applications":
        this.openGenericModal(
          "application_create",
          { title: "Add Application", saveBtnText: "Add" },
          item,
          template
        );
        break;
      case "application":
        this.openGenericModal(
          "page_create",
          { title: "Add New Page", saveBtnText: "Add" },
          item,
          template
        );
        break;
      case "pages":
        this.openGenericModal(
          "ui_object_create",
          { title: "Add New UI object", saveBtnText: "Add" },
          item,
          template
        );
        break;
      case "reusable_component":
        this.openGenericModal(
          "reusable_component",
          {
            title: "Add Reusable Component Folder",
            // title: "Add Reusable Components/Component Folder",
            saveBtnText: "Add",
          },
          item,
          template
        );
        break;
      case "script_group":
        this.openGenericModal(
          item?.type,
          {
            title: "Add New Test Suite/Test Script",
            saveBtnText: "Add",
            step: " Step 1/2",
          },
          item,
          template
        );
        break;
      case "test_script_group":
        this.openGenericModal(
          "test_script_create",
          {
            title: "Add New Test Script",
            saveBtnText: "Add",
            step: " Step 1/2",
          },
          item,
          template
        );
        break;
      case "reusable_component_folder":
        this.openGenericModal(
          "reusable_script_create",
          {
            title: "Add New Reusable Script",
            saveBtnText: "Add",
          },
          item,
          template
        );
        break;
      default:
        break;
    }
  }

  renamePopupTemplates(item: any) {
    const template = [...(item?.editApplicationTemplate ?? [])];

    switch (item?.type) {
      case Actions.TEST_SCRIPT_GROUP:
        template.forEach((obj) => {
          obj.value = item[obj.id];
        });

        this.openGenericModal(
          "script_group_edit",
          { title: "Edit Script Group", isEdit: true },
          item,
          template
        );
        break;

      case Actions.SCRIPT:
        template.forEach((obj) => {
          obj.value = item[obj.id];
        });

        this.openGenericModal(
          "test_script_edit",
          { title: "Edit Test Script", isEdit: true },
          item,
          template
        );
        break;

      case Actions.REUSABLE_SCRIPT:
        template.forEach((obj) => {
          obj.value = item[obj.id];
        });

        this.openGenericModal(
          "test_script_edit",
          { title: "Edit Reusable Script", isEdit: true },
          item,
          template
        );
        break;

      case Actions.REUSABLE_COMPONENT_FOLDER:
        template.forEach((obj) => {
          obj.value = item[obj.id];
        });

        this.openGenericModal(
          "reusable_component_folder_edit",
          { title: "Edit Reusable Component Folder", isEdit: true },
          item,
          template
        );
        break;

      case Actions.APPLICATION:
        template.forEach((obj) => {
          obj.value = item[obj.id];
        });

        this.openGenericModal(
          "application_edit",
          { title: "Edit Application", isEdit: true },
          item,
          template
        );
        break;

      case Actions.PAGES:
        template.forEach((obj) => {
          obj.value = item[obj.id];
        });
        this.openGenericModal(
          "page_edit",
          { title: "Edit Page", isEdit: true },
          item,
          template
        );
        break;

      default:
        break;
    }
  }

  onActionFireEvents(MouseEvent: any, type: any, cardEvent: any) {
    console.log(MouseEvent, type, cardEvent);
    switch (type) {
      case Actions.PROJECT_DETAIL_VIEW:
        this.loadProjectDetails(cardEvent);
        this.projectService.setAutomationProjectId(cardEvent?.id);
        break;
      case Actions.DELETE:
        this.deleteItems(cardEvent);
        break;
      default:
        break;
    }
  }

  loadTreeData(item: any) {
    switch (item.type) {
      case Actions.PROJECT:
        this.loadProjectDetails(item);
        break;
      case Actions.APPLICATIONS:
        this.store.dispatch(
          // this.utilityService.dispatchIfEmpty(
          AutomationActions.loadApplications({
            projectId: this.projectId,
            automationProjectId: this.automationProjectID,
          })
        );
        break;
      case Actions.APPLICATION:
        this.store.dispatch(
          // this.utilityService.dispatchIfEmpty(
          AutomationActions.loadPages({
            projectId: this.projectId,
            automationProjectId: this.automationProjectID,
            applicationId: item?.id,
          })
        );
        break;
      case Actions.PAGES:
        this.store.dispatch(
          // this.utilityService.dispatchIfEmpty(
          // this.uiObjects$,
          AutomationActions.loadUiObjects({
            projectId: this.projectId,
            automationProjectId: this.automationProjectID,
            applicationId: this.getApplicationId(item?.id),
            pageId: item?.id,
          })
        );
        break;

      case Actions.SCRIPT_GROUP:
        // this.utilityService.dispatchIfEmpty(
        this.store.dispatch(
          // this.scriptGroup$,
          AutomationActions.loadScriptGroup({
            projectId: this.projectId,
            automationProjectId: this.automationProjectID,
          })
        );
        break;
      case Actions.REUSABLE_COMPONENT:
        this.store.dispatch(
          AutomationActions.loadReusableComponent({
            projectId: this.projectId,
            automationProjectId: this.automationProjectID,
          })
        );
        break;
      case Actions.API_SERVICE:
        this.store.dispatch(
          AutomationActions.loadService({
            projectId: this.projectId,
            automationProjectId: this.automationProjectID,
          })
        );
        break;
      case Actions.API_SERVICE_VIEW:
        this.store.dispatch(
          AutomationActions.loadServiceOperation({
            projectId: this.projectId,
            automationProjectId: this.automationProjectID,
            serviceId: this.serviceId,
          })
        );
        break;
      case Actions.OPERATION_VIEW:
        this.store.dispatch(
          AutomationActions.loadOperationParameters({
            projectId: this.projectId,
            automationProjectId: this.automationProjectID,
            serviceId: this.serviceId,
            operationId: this.operationId,
          })
        );
        this.store.dispatch(
          AutomationActions.loadOperationHeader({
            projectId: this.projectId,
            automationProjectId: this.automationProjectID,
            serviceId: this.serviceId,
            operationId: this.operationId,
          })
        );
        break;
      default:
        break;
    }
  }

  openGenericModal(
    actionType: string,
    modalParams: any,
    row: any,
    fields?: any[]
  ) {
    const modalRef = this.modalService.open(EditorItemModalComponent, {
      centered: true,
      windowClass: "add-item-modal",
      backdrop: 'static',
    });
    modalRef.componentInstance.configs = modalParams;
    if (fields) {
      modalRef.componentInstance.fields = fields;
    }
    modalRef.result.then((result) => {
      console.log("actionType", actionType, result);
      switch (actionType) {
        case Actions.APPLICATION_CREATE:
          this.store.dispatch(
            AutomationActions.addApplication({
              projectId: this.projectId,
              automationProjectId: this.automationProjectID,
              application: {
                name: result?.value?.name,
                paradigm: result?.value?.type,
                browser: result?.value?.browser,
              },
            })
          );
          break;

        case Actions.APPLICATION_EDIT:
          this.store.dispatch(
            AutomationActions.updateApplication({
              projectId: this.projectId,
              automationProjectId: this.automationProjectID,
              id: row?.id,
              changes: {
                name: result?.value?.name,
                paradigm: result?.value?.paradigm,
                browser: result?.value?.browser,
              },
            })
          );
          break;

        case Actions.PAGE_CREATE:
          if (
            this.checkUrl(result?.value?.url) ||
            this.isInvalidString(result?.value?.name)
          ) {
            this.toaster.pop({
              type: "error",
              text: this.checkUrl(result?.value?.url)
                ? MessageEnum.PAGE_URL_INVALID
                : MessageEnum.PAGE_NAME_INVALID,
            });
          } else {
            this.store.dispatch(
              AutomationActions.addPage({
                projectId: this.projectId,
                automationProjectId: this.automationProjectID,
                applicationId: row?.id,
                page: {
                  name: result?.value?.pageName,
                  url: result?.value?.url,
                },
              })
            );
          }
          break;

        case Actions.PAGE_EDIT:
          if (
            this.checkUrl(result?.value?.url) ||
            this.isInvalidString(result?.value?.name)
          ) {
            this.toaster.pop({
              type: "error",
              text: this.checkUrl(result?.value?.url)
                ? MessageEnum.PAGE_URL_INVALID
                : MessageEnum.PAGE_NAME_INVALID,
            });
          } else {
            this.store.dispatch(
              AutomationActions.updatePage({
                projectId: this.projectId,
                automationProjectId: this.automationProjectID,
                applicationId: this.getApplicationId(row?.id),
                id: row?.id,
                changes: {
                  name: result?.value?.name,
                  url: result?.value?.url,
                },
              })
            );
          }
          break;

        case Actions.UI_OBJECT_CREATE:
          this.store.dispatch(
            AutomationActions.addUiObject({
              projectId: this.projectId,
              automationProjectId: this.automationProjectID,
              applicationId: this.getApplicationId(row.id),
              pageId: row?.id,
              uiObject: {
                name: result?.value?.name,
                locators: [],
              },
            })
          );
          break;
        case Actions.SCRIPT_GROUP:
          const type = result?.fields.find((item: any) => item.key === "type");
          if (type?.value === "test_suite") {
            this.store.dispatch(
              AutomationActions.addScriptGroup({
                projectId: this.projectId,
                automationProjectId: this.automationProjectID,
                scriptGroup: {
                  name: result?.value?.test_suite_name,
                  type: "SCRIPT",
                },
              })
            );
          } else {
            this.isTestcaseMapping = true;
            this.store.dispatch(
              AutomationActions.addTestScript({
                projectId: this.projectId,
                automationProjectId: this.automationProjectID,
                testScript: {
                  name: result?.value?.test_script_name,
                  type: "SCRIPT",
                },
              })
            );
          }
          break;

        case Actions.SCRIPT_GROUP_EDIT:
          this.store.dispatch(
            AutomationActions.updateScriptGroup({
              projectId: this.projectId,
              automationProjectId: this.automationProjectID,
              id: row?.id,
              changes: {
                name: result?.value?.name,
                type: "SCRIPT",
              },
            })
          );
          break;

        case Actions.TEST_SCRIPT_CREATE:
          this.isTestcaseMapping = true;
          this.store.dispatch(
            AutomationActions.addTestScript({
              projectId: this.projectId,
              automationProjectId: this.automationProjectID,
              testScript: {
                name: result?.value?.name,
                type: "SCRIPT",
                automationGroupId: row?.id,
              },
            })
          );
          //this.openTestScriptMappingView(row);
          break;

        case Actions.TEST_SCRIPT_EDIT:
          this.store.dispatch(
            AutomationActions.renameTestScript({
              projectId: this.projectId,
              automationProjectId: this.automationProjectID,
              id: row?.id,
              changes: {
                name: result?.value?.name,
                type: "SCRIPT",
                automationGroupId: row?.automationScriptGroup?.id,
                entityUsageList: [],
              },
            })
          );
          break;

        case Actions.REUSABLE_COMPONENT:
          const reusableType = result?.fields.find(
            (item: any) => item.key === "reusable_components_type"
          );
          if (reusableType?.value === "reusable_components_folder") {
            this.store.dispatch(
              AutomationActions.addReusableComponent({
                projectId: this.projectId,
                automationProjectId: this.automationProjectID,
                reusableComponent: {
                  name: result?.value?.folder_name,
                  type: "REUSABLE_SCRIPT",
                },
              })
            );
          } else {
            this.store.dispatch(
              AutomationActions.addReusableScript({
                projectId: this.projectId,
                automationProjectId: this.automationProjectID,
                reusableScript: {
                  name: result?.value?.component_name,
                  type: "REUSABLE_SCRIPT",
                },
              })
            );
          }
          break;

        case Actions.REUSABLE_COMPONENT_FOLDER_EDIT:
          this.store.dispatch(
            AutomationActions.updateReusableComponent({
              projectId: this.projectId,
              automationProjectId: this.automationProjectID,
              id: row?.id,
              changes: {
                name: result?.value?.name,
                type: "REUSABLE_SCRIPT",
              },
            })
          );
          break;

        case Actions.REUSABLE_SCRIPT_CREATE:
          this.store.dispatch(
            AutomationActions.addReusableScript({
              projectId: this.projectId,
              automationProjectId: this.automationProjectID,
              reusableScript: {
                name: result?.value?.name,
                type: "REUSABLE_SCRIPT",
                automationGroupId: row?.id,
              },
            })
          );
          break;

        case Actions.REUSABLE_SCRIPT_EDIT:
          this.store.dispatch(
            AutomationActions.updateReusableScript({
              projectId: this.projectId,
              automationProjectId: this.automationProjectID,
              id: row?.id,
              changes: {
                name: result?.value?.name,
                type: "REUSABLE_SCRIPT",
                automationGroupId: row?.id,
              },
            })
          );
          break;

        default:
          break;
      }
    });
  }

  //check for white spaces in url
  checkUrl(url: string) {
    return /\s/.test(url);
  }

  //check for invalid string
  isInvalidString(input: string): boolean {
    const regex = /^[a-zA-Z0-9_]+$/; // Allows only letters, numbers, and '_'
    return !regex.test(input);
  }

  // Project related CRUD
  loadInitialProjct() {
    this.loading = true;
    // A3-5490 stale guard: if the user navigates to a different project before
    // this response arrives, discard it to avoid clobbering the new project state.
    const capturedProjectId = this.projectId;
    this.restService.getAutomationProject(capturedProjectId).subscribe(
      (response: any) => {
        if (capturedProjectId !== this.projectId) return; // stale response
        const data = response?.data[0];
        this.projectService.setAutomationProjectId(data?.id);
        if (data) {
          this.treeData = [
            getApplicationDetails(),
            getReusableComponentsDetails(),
            getStandardTestSuitsDetails(),
            getServicesDetails()
          ];
          // this.scriptCards = this.transformProjectCardOverview(data);
          this.loadProjectDetails(data);
          // load Test Suit details
          // this.store.dispatch(
          //   AutomationActions.loadScriptGroup({
          //     projectId: this.projectId,
          //     automationProjectId: this.automationProjectID,
          //   })
          // );
        }
        this.loading = false;
      },
      (error: any) => {
        this.loading = false;
        console.log(error);
      }
    );
  }

  loadProjectDetails(data: any) {
    this.loading = true;
    this.isFrameworkTypeLoading = true;
    this.restService
      .getAutomationProjectDetails(this.projectId, data?.id)
      .subscribe(
        (response: any) => {
          const data = response?.data;
          if (data) {
            this.isFrameworkTypeLoading = false;
            const updatedProjectDetails = this.transformProjectDetails(data);
            const indexToUpdate = this.treeData.findIndex(
              (item) => item.id === data.id
            );
            this.selectedFramworkType = data?.frameworkType;
            this.projectService.setFrameworkType(this.selectedFramworkType);

            if (indexToUpdate !== -1) {
              this.treeData[indexToUpdate] = updatedProjectDetails;
            }
            this.selectedItem = updatedProjectDetails;
            this.loadComponent();
            this.loading = false;
          }
        },
        (error: any) => {
          console.log(error);
          this.loading = false;
          this.isFrameworkTypeLoading = false;
        }
      );
  }

  // Data transformation
  transformDataToProjectHirachy(data: any[]) {
    const treeView: any = [];
    data.forEach((element: any) => {
      const treeData = { ...initialProjectStructure };
      treeData.name = element?.name;
      treeData.displayName = element?.name;
      treeData.id = element?.id;
      treeView.push(treeData);
    });

    return treeView;
  }

  transformProjectCardOverview(data: any[]) {
    const cardData: any = this.scriptCards;
    data.forEach((item) => {
      const data = { ...projectCardTile };
      data.title = item?.name;
      data.description = item?.description;
      data.id = item?.id;
      cardData.push(data);
    });
    return cardData;
  }

  transformProjectDetails(data: any) {
    const treeData = { ...initialProjectStructure };
    treeData.name = data?.name;
    treeData.displayName = data?.name;
    treeData.id = data?.id;
    return treeData;
  }

  /**
   * Confirms and deletes the selected item (organization or project) after user confirmation.
   *
   * This method opens a confirmation dialog and deletes the item if the user accepts the action.
   *
   * @param {ITileData} cardData - The data related to the item to be deleted.
   * @param {Event} [event] - The event associated with the delete action.
   */
  deleteItems(data: any) {
    let modalRef = this.modalService.open(DeleteConfirmationComponent, {
      windowClass:"delete-confirmation-window",
      centered: true,
      size: "sm",
      backdrop: "static",
    });
    modalRef.componentInstance.data = {
      title: "Confirm Deletion",
      description: `Are you sure you want to delete ${
        data.name || data.title
      }?`,
    };
    modalRef.result.then((result) => {
      if (result) {
        switch (data?.type) {
          case Actions.APPLICATION:
            this.store.dispatch(
              AutomationActions.deleteApplication({
                projectId: this.projectId,
                automationProjectId: this.automationProjectID,
                id: data?.id,
              })
            );
            break;
          case Actions.TEST_SCRIPT_GROUP:
            this.store.dispatch(
              AutomationActions.deleteScriptGroup({
                projectId: this.projectId,
                automationProjectId: this.automationProjectID,
                id: data?.id,
              })
            );
            break;
          case Actions.SCRIPT:
            this.store.dispatch(
              AutomationActions.deleteTestScript({
                projectId: this.projectId,
                automationProjectId: this.automationProjectID,
                id: data?.id,
              })
            );
            break;
          case Actions.REUSABLE_SCRIPT:
          case Actions.REUSABLE_SCRIPT_FILE:
            this.store.dispatch(
              AutomationActions.deleteReusableScript({
                projectId: this.projectId,
                automationProjectId: this.automationProjectID,
                id: data?.id,
              })
            );
            break;
          case Actions.REUSABLE_COMPONENT_FOLDER:
            this.store.dispatch(
              AutomationActions.deleteReusableComponent({
                projectId: this.projectId,
                automationProjectId: this.automationProjectID,
                id: data?.id,
              })
            );
            break;

          case Actions.PAGES:
            this.store.dispatch(
              AutomationActions.deletePage({
                projectId: this.projectId,
                automationProjectId: this.automationProjectID,
                applicationId: this.getApplicationId(data?.id),
                id: data?.id,
              })
            );
            break;

          // case Actions.UI_OBJECT_DELETE:
          //   this.store.dispatch(
          //     AutomationActions.deleteUiObject({
          //       projectId: this.projectId,
          //       automationProjectId: this.automationProjectID,
          //       applicationId: this.applicationId,
          //       pageId: this.pageId,
          //       id: data?.id,
          //     })
          //   );
          //   break;
          default:
            break;
        }
        setTimeout(() => {
          if (data?.id === this.selectedItem?.id) {
            this.selectedItem = undefined;
          }
        }, 1000);
      }
    });
  }

  getApplicationId(nodeId: string) {
    let applicationId: any = "";
    const applicationNode: any = this.treeData.filter(
      (item) => item.type === "applications"
    );
    if (applicationNode) {
      applicationNode[0]?.children?.forEach((item: any) => {
        const pageIndex = item?.children.findIndex(
          (page: any) => page.id === nodeId
        );
        if (pageIndex > -1) {
          applicationId = item.id;
        }
      });
    }
    return applicationId;
  }

  /**
   * Clears all local view state so that a freshly-loaded project starts from
   * a blank canvas. Called before loadInitialProjct() on every project switch.
   */
  resetGeneratorView(): void {
    this.selectedItem = undefined;
    this.automationProjectID = null;
    this.testSuits = [];
    this.reusableComponents = [];
    this.serviceComponents = [];
    this.scriptCards = [];
    this.treeData = [];
    this.appGenericView?.clear();
    this.breadcrumbService.clear();
  }

  ngOnDestroy() {
    this.pageActionSub?.unsubscribe();
    this.subscriptions.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    this.breadcrumbService.clearPageActions();
  }

  openExport() {
    if (this.isExporting || !this.projectId || !this.automationProjectID) {
      return;
    }
    const modalRef = this.modalService.open(GenericFormModalComponent, {
      centered: true,
      windowClass: "generic-form-window",
      backdrop: "static",
    });
    modalRef.componentInstance.configs = {
      title: "Export automation project",
      saveBtnText: "Export",
    };
    modalRef.componentInstance.fields = [
      {
        key: "includeSecrets",
        name: "Include secrets",
        type: "checkbox",
        value: true,
        tooltip:
          "Includes application credential passwords, API authentication secrets, and data-connection passwords.",
      },
    ];
    modalRef.result.then((result) => {
      if (result) {
        this.exportAutomationProject(!!result.includeSecrets);
      }
    }).catch(() => {});
  }

  openImport() {
    if (this.isImporting || !this.projectId) {
      return;
    }
    const modalRef = this.modalService.open(GenericFormModalComponent, {
      centered: true,
      windowClass: "generic-form-window",
      backdrop: "static",
    });
    modalRef.componentInstance.configs = {
      title: "Import automation project",
      saveBtnText: "Import",
    };
    modalRef.componentInstance.fields = [
      {
        type: "section-label",
        hideLabel: true,
        value:
          "Import is available only when this project has no applications, scripts, services, or other content.",
        customClass: "general-text-6",
      },
      {
        key: "file",
        name: "Zip file",
        type: "file-upload",
        validations: { required: true },
        configs: {
          fileTypes: ".zip,application/zip",
          placeholderText: "Choose the export .zip file",
          buttonlabelPrefix: "",
        },
      },
    ];
    modalRef.result.then((result) => {
      const file = result?.file as File;
      if (file) {
        this.importAutomationProject(file);
      }
    }).catch(() => {});
  }

  private exportAutomationProject(includeSecrets: boolean): void {
    this.isExporting = true;
    this.restService
      .exportAutomationProject(this.projectId, this.automationProjectID, includeSecrets)
      .subscribe({
        next: (response: HttpResponse<Blob>) => {
          this.isExporting = false;
          this.saveZipDownload(response);
          this.toaster.pop({ type: "success", text: "Automation project exported." });
        },
        error: (error: { message?: string; errors?: string[] }) => {
          this.isExporting = false;
          this.showTransferError(error, "Export failed.");
        },
      });
  }

  private importAutomationProject(file: File, name?: string): void {
    this.isImporting = true;
    this.restService.importAutomationProject(this.projectId, file, name).subscribe({
      next: (response: any) => {
        this.isImporting = false;
        const data = response?.data;
        const warnings: string[] = Array.isArray(data?.warnings) ? data.warnings : [];
        if (data?.automationProjectId) {
          this.automationProjectID = data.automationProjectId;
          this.projectService.setAutomationProjectId(data.automationProjectId);
        }
        this.selectedItem = undefined;
        this.appGenericView?.clear();
        this.loadInitialProjct();
        this.reloadAutomationLists();
        if (warnings.length) {
          this.toaster.pop(
            { type: "warning", text: warnings.join(" ") },
            { timeout: 8000 }
          );
        } else {
          this.toaster.pop({
            type: "success",
            text: response?.message || "Automation project imported successfully",
          });
        }
      },
      error: (error: { message?: string; errors?: string[] }) => {
        this.isImporting = false;
        this.showTransferError(error, "Import failed.");
      },
    });
  }

  private reloadAutomationLists(): void {
    const projectId = this.projectId;
    const automationProjectId = this.automationProjectID;
    if (!projectId || !automationProjectId) {
      return;
    }
    this.store.dispatch(AutomationActions.loadApplications({ projectId, automationProjectId }));
    this.store.dispatch(AutomationActions.loadScriptGroup({ projectId, automationProjectId }));
    this.store.dispatch(AutomationActions.loadReusableComponent({ projectId, automationProjectId }));
    this.store.dispatch(AutomationActions.loadService({ projectId, automationProjectId }));
  }

  private saveZipDownload(response: HttpResponse<Blob>): void {
    const blob = response.body;
    if (!blob) {
      return;
    }
    const header = response.headers.get("Content-Disposition") || "";
    const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(header);
    const filename = decodeURIComponent((match?.[1] || match?.[2] || "automation-project-export.zip").trim());
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  private showTransferError(error: { message?: string; errors?: string[] }, fallback: string): void {
    const details = (error?.errors || []).filter((item) => !!item);
    const text = [error?.message || fallback, ...details].join(" ");
    this.toaster.pop({ type: "error", text });
  }

  downloadProject() {
    this.isDownloading = true;
    this.restService
      .downloadAutomationProject(this.projectId, this.automationProjectID)
      .subscribe(
        (item: any) => {
          this.isDownloading = false;
          window.open(item?.data?.signedUrl, "_blank");
        },
        (error) => {
          this.isDownloading = false;
          this.toaster.pop({
            type: "error",
            text: error?.message
              ? error.message
              : MessageEnum.PROJECT_DOWNLOAD_FAILURE,
          });
        }
      );
  }

  openConfiguration() {
    let modalRef = this.modalService.open(GenericFormModalComponent, {
      centered: true,
      size: "lg",
      windowClass: "generic-form-window",
      backdrop: 'static',
    });
    modalRef.componentInstance.configs = {
      title: "Script Generator Configuration",
      isLoading: true,
    };

    this.restService.getFrameworkList(this.projectId).subscribe((item) => {
      modalRef.componentInstance.fields = [
        {
          type: "section-label",
          value: "Framework Settings",
          customClass: "header-19",
        },
        {
          key: "frameworkType",
          name: "Framework Type",
          type: "select",
          placeholder: "Select framework type",
          options: item?.data.map((item: any) => ({
            value: item,
            label: item,
          })),
          value: this.selectedFramworkType,
          validations: {
            required: true,
          },
          // customClass: "col-md-6",
        },
      ];
      modalRef.componentInstance.configs.isLoading = false;
    });

    modalRef.result.then((result) => {
      console.log("Result", result);
      this.restService
        .updateFrameworkType(this.projectId, this.automationProjectID, {
          frameworkType: result?.frameworkType,
        })
        .subscribe(
          (item) => {
            console.log("DATA SAVED", item);
            this.toaster.pop({ type: 'success', text: MessageEnum.FRAMEWORK_SUBMIT_SUCCESS });
            this.selectedFramworkType = item?.data?.frameworkType;
            this.projectService.setFrameworkType(this.selectedFramworkType);
          },
          (error) => {
            this.toaster.pop({ type: 'error', text: MessageEnum.FRAMEWORK_SUBMIT_FAILURE });
          }
        );
    });
  }
}
function findTreePath<T>(
  treeData: T[],
  predicate: (node: T) => boolean
): T[] {
  for (const node of treeData ?? []) {
    if (predicate(node)) {
      return [node];
    }

    const children = (node as any)?.children;
    if (Array.isArray(children)) {
      const childPath = findTreePath<T>(children, predicate);
      if (childPath.length) {
        return [node, ...childPath];
      }
    }
  }

  return [];
}

